import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Award } from "lucide-react";

/**
 * Subscribes to user_badges and user_achievements inserts/updates for the
 * current user and surfaces a celebratory toast when one fires.
 *
 * Mounted globally inside AppShellLayout.
 */
export function useBadgeNotifications(userId: string | undefined) {
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userId) return;

    const badgeChannel = supabase
      .channel(`badge-notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_badges",
          filter: `user_id=eq.${userId}`,
        },
        async (payload) => {
          const row = payload.new as { id: string; badge_id: string };
          if (seenRef.current.has(row.id)) return;
          seenRef.current.add(row.id);

          const { data: badge } = await supabase
            .from("badges")
            .select("name, description, points")
            .eq("id", row.badge_id)
            .maybeSingle();
          if (!badge) return;

          toast.success(`Badge earned: ${badge.name}`, {
            description: badge.description ?? `+${badge.points} XP — Silva Coins added`,
            icon: <Award className="h-4 w-4 text-primary" />,
            duration: 6000,
          });
        }
      )
      .subscribe();

    const achievementChannel = supabase
      .channel(`achievement-notifications-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_achievements",
          filter: `user_id=eq.${userId}`,
        },
        async (payload) => {
          const row = payload.new as {
            id: string;
            achievement_id: string;
            completed_at: string | null;
          };
          const old = payload.old as { completed_at: string | null };
          if (!row.completed_at || old.completed_at) return;
          if (seenRef.current.has(`a:${row.id}`)) return;
          seenRef.current.add(`a:${row.id}`);

          const { data: ach } = await supabase
            .from("achievements")
            .select("name, description, points")
            .eq("id", row.achievement_id)
            .maybeSingle();
          if (!ach) return;

          toast.success(`Achievement unlocked: ${ach.name}`, {
            description: ach.description ?? `+${ach.points} XP`,
            icon: <Award className="h-4 w-4 text-amber-500" />,
            duration: 6000,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(badgeChannel);
      supabase.removeChannel(achievementChannel);
    };
  }, [userId]);
}
